import React from 'react';

const BG = '#7f8a4a';

/**
 * Shared microlearning landing card.
 * Used by /private-sale, /villagebike, /private-sale-hosts, /rushhour.
 *
 * buttons: array of { label, href, variant?: 'primary' | 'outline', testid? }
 */
const LearningCard = ({ testid, title, subtitle, support, buttons, logoSrc = '/logo-mmv.png', logoAlt = 'Logo', logoRound = true }) => {
  return (
    <div
      data-testid={`${testid}-page`}
      className="min-h-screen w-full flex items-center justify-center px-6 py-12"
      style={{ backgroundColor: BG }}
    >
      <div
        data-testid={`${testid}-card`}
        className="w-full max-w-md mx-auto bg-white/95 backdrop-blur-sm rounded-3xl shadow-2xl px-8 py-10 sm:px-10 sm:py-12 text-center"
      >
        <img
          src={logoSrc}
          alt={logoAlt}
          data-testid={`${testid}-logo`}
          className={
            logoRound
              ? 'w-20 h-20 sm:w-24 sm:h-24 mx-auto mb-8 rounded-full object-cover'
              : 'h-12 sm:h-14 w-auto mx-auto mb-8 object-contain'
          }
        />

        <h1
          data-testid={`${testid}-title`}
          className="text-2xl sm:text-3xl font-semibold tracking-tight text-neutral-900"
          style={{ fontFamily: "'Inter', system-ui, -apple-system, sans-serif" }}
        >
          {title}
        </h1>

        {subtitle && (
          <p
            data-testid={`${testid}-subtitle`}
            className="mt-4 text-base sm:text-lg text-neutral-700 leading-relaxed"
          >
            {subtitle}
          </p>
        )}

        {support && (
          <div
            data-testid={`${testid}-support`}
            className="mt-3 text-sm text-neutral-500 leading-relaxed space-y-3"
          >
            {Array.isArray(support)
              ? support.map((paragraph) => <p key={paragraph}>{paragraph}</p>)
              : <p>{support}</p>}
          </div>
        )}

        <div className="mt-10 flex flex-col gap-4">
          {buttons.map((btn) => {
            const isOutline = btn.variant === 'outline';
            const baseClasses =
              'w-full inline-flex items-center justify-center rounded-full px-6 py-4 text-base font-semibold transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/40';
            const style = isOutline
              ? { color: BG, borderColor: BG, backgroundColor: 'transparent' }
              : { backgroundColor: BG };
            const variantClasses = isOutline
              ? 'border-2'
              : 'text-white shadow-md hover:shadow-xl';

            return (
              <a
                key={btn.href}
                href={btn.href}
                data-testid={btn.testid}
                className={`${baseClasses} ${variantClasses}`}
                style={style}
              >
                {btn.label}
              </a>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LearningCard;
